import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { NumericFormat } from 'react-number-format'

import Checkbox from '../../../../../../components/form/Checkbox'

import MoneyInput from '../../../../../../components/form/MoneyInput'
import LoadingIcon from '../../../../../../components/LoadingIcon'
import { CurrencyList, ContractPosition } from '../../../types'

import { PerformanceContractDocPositionDraft } from '../types'

import HoverTooltip from '../../../../../../components/HoverTooltip'
import PerformanceDocsAddFromContractPopupTableRow from './PerformanceInfoTable/PerformanceDocsAddFromContractPopupTableRow'

const PERFORMANCE_CONTRACT_DOC_CODE: CodeItem[] = [
    { code: '01', name: 'товарная накладная' },
    { code: '02', name: 'товарно-транспортная накладная' },
    { code: '03', name: 'железнодорожная накладная' },
    { code: '04', name: 'авианакладная' },
    { code: '05', name: 'коносамент' },
    { code: '06', name: 'акт о приемке товаров' },
    {
        code: '07',
        name: 'акт о приемке товаров, поступившего без счета поставщика',
    },
    {
        code: '08',
        name: 'акт о приемке-передаче объекта основных средств (кроме зданий, сооружений)',
    },
    { code: '09', name: 'акт о приемке-передаче здания (сооружения)' },
    {
        code: '10',
        name: 'акт о приемке-передаче групп объектов основных средств (кроме зданий, сооружений)',
    },
    {
        code: '11',
        name: 'акт о приеме-сдаче отремонтированных, реконструированных модернизированных объектов основных средств',
    },
    { code: '12', name: 'акт о приемке выполненных работ' },
    { code: '13', name: 'акт приемке законченного строительством объекта' },
    { code: '18', name: 'счет на оплату' },
    { code: '19', name: 'счет-фактура' },
    { code: '20', name: 'акт выполненных работ' },
    { code: '21', name: 'акт о приемке материалов' },
    { code: '22', name: 'акт о завесе тары' },
    {
        code: '23',
        name: 'акт об оприходовании тары, не указанной в счете поставщика',
    },
    {
        code: '24',
        name: 'акт о выявлении недостатков выполненных работ (оказанных услуг)',
    },
    { code: '25', name: 'акт о невыполнении работ (оказании услуг)' },
    { code: '26', name: 'акт - рекламация' },
    { code: '31', name: 'акт о приеме (поступлении) оборудования' },
    { code: '37', name: 'прочие документы о приемке' },
]

type Props = {
    theme: string
    currencyList: CurrencyList[]
    contractGuid: string | undefined
    contractRegNumber: string | undefined
    handlePerformanceDocsAddFromContractPopup(): void
    onAddItems: (rows: ContractPosition[]) => void
}

interface CodeItem {
    code: string
    name: string
}

const PerformanceDocsAddFromContractPopup: React.FC<Props> = (props) => {
    const [contractPosition, setContractPosition] = useState<
        ContractPosition[] | null
    >()
    const [isLoadingContractPosition, setLoadingContractPosition] =
        useState(false)

    const [selectedItems, setSelectedItems] = useState<string[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const [search, setSearch] = useState('')

    const loadContractPosition = () => {
        setLoadingContractPosition(true)

        fetch(
            process.env.REACT_APP_API_URL +
                `/api/backend/v1/contract_position/contract_guid/${props.contractGuid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Failed to fetch count')
                }
            })
            .then((data) => {
                // Обновляем состояния
                setContractPosition(data)
            })
            .catch((e) => {
                console.error(e)
                // Устанавливаем значения по умолчанию на случай ошибки
                setContractPosition(null)
            })
            .finally(() => {
                setLoadingContractPosition(false)
            })
    }

    // Функция для обработки изменения состояния отдельного checkbox
    const handleCheckboxChange = (contract_position_guid: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(contract_position_guid)) {
                return prevSelectedItems.filter(
                    (itemId) => itemId !== contract_position_guid
                )
            } else {
                return [...prevSelectedItems, contract_position_guid]
            }
        })
    }

    // Функция для обработки изменения состояния отдельного checkbox при нажатии правой кнопкой
    const handleRightClickCheckboxChange = (contract_position_guid: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(contract_position_guid)) {
                return prevSelectedItems // Если уже выбран, ничего не меняем
            }
            return [...prevSelectedItems, contract_position_guid] // Добавляем в выбранные
        })
    }

    // Функция для обработки изменения состояния "Select All"
    const handleSelectAllChange = () => {
        setSelectAll(!selectAll)
        if (!selectAll) {
            // Выбираем только те элементы, которые соответствуют текущей стадии
            const filteredItems = contractPosition
                ? contractPosition.map((item) => item.guid ?? '')
                : []

            setSelectedItems(filteredItems)
        } else {
            // Сбрасываем выбор всех элементов
            setSelectedItems([])
        }
    }

    // безопасный парсер десятичных (точка)
    const parseDec = (v: string | number | null | undefined) => {
        if (v === null || v === undefined) return undefined
        if (typeof v === 'number') return v
        const num = Number(String(v).replace(',', '.'))
        return Number.isNaN(num) ? undefined : num
    }

    // Фильтрация по поиску
    const filteredList = useMemo(() => {
        if (contractPosition) {
            const q = (search ?? '').trim().toLowerCase()
            return contractPosition.filter((c) => {
                const name = String(c?.contract_position_name ?? '')
                return q ? name.toLowerCase().includes(q) : true
            })
        }
    }, [contractPosition, search])

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (props.contractGuid) {
                    loadContractPosition()
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    return (
        <div className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center">
            <div
                className="flex flex-col p-5 rounded-lg w-[900px] h-fit gap-y-5 bg-[#e5e5e5] dark:bg-[#262427]"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="text-[#181818] dark:text-[#FFFFFF]">
                    Выберите позицию договора
                </h2>

                <div className="flex flex-col w-full h-fit items-center justify-center bg-[#e4e6e8] dark:bg-[#242225] rounded">
                    <div className="flex flex-row gap-1 p-1 w-full h-[40px] text-[11px] rounded-t text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033] items-center justify-center">
                        <div className="flex w-8 h-full items-center justify-center text-center">
                            <HoverTooltip tooltipText="Выбрать все">
                                <div
                                    className="flex w-6 h-6 rounded-full cursor-pointer items-center justify-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={handleSelectAllChange}
                                >
                                    <input
                                        type="checkbox"
                                        className="p-2 cursor-pointer"
                                        checked={selectAll}
                                    />
                                </div>
                            </HoverTooltip>
                        </div>
                        <div className="flex flex-1 w-full h-full items-center justify-center text-center"></div>
                    </div>
                    <div className="flex flex-row gap-1 w-full h-[40px] p-1 px-2 items-center justify-center text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2a282b]">
                        <div className="flex w-8 h-full items-center justify-center text-center"></div>
                        <div className="flex w-10 h-full items-center justify-center text-center">
                            №
                        </div>
                        <div className="flex flex-1 items-center justify-center ">
                            Наименование
                        </div>
                        <div className="flex w-[100px] items-center justify-center ">
                            Единица изменения
                        </div>
                        <div className="flex w-[100px] items-center justify-center ">
                            Количество
                        </div>
                        <div className="flex w-[100px] items-center justify-center ">
                            Цена за единицу
                        </div>
                    </div>
                    <div className="flex flex-row gap-1 w-full p-1 px-2 items-center justify-center text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2a282b]">
                        <input
                            type="text"
                            className="form-textarea-table-row flex min-h-[28px] w-full p-1 px-2 rounded-md text-[12.8px] text-[#181818] dark:text-[#FFFFFF] bg-[#181818]"
                            placeholder="Поиск"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <div className="flex w-10 justify-center items-center text-center">
                            <HoverTooltip
                                tooltipText="Очистить фильтр"
                                align="left"
                            >
                                <button
                                    className="w-6 h-6 grid rounded-full place-items-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={() => setSearch('')}
                                >
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        data-name="Layer 1"
                                        viewBox="0 0 24 24"
                                        id="filter_clear"
                                    >
                                        <path
                                            fill={
                                                props.theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d="M8 19a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3 1 1 0 0 0 0-2 5 5 0 0 0-5 5v8a5 5 0 0 0 5 5 1 1 0 0 0 0-2Zm7.71-3.29a1 1 0 0 0 0-1.42L13.41 12l2.3-2.29a1 1 0 0 0-1.42-1.42L12 10.59l-2.29-2.3a1 1 0 0 0-1.42 1.42l2.3 2.29-2.3 2.29a1 1 0 0 0 0 1.42 1 1 0 0 0 1.42 0l2.29-2.3 2.29 2.3a1 1 0 0 0 1.42 0ZM16 3a1 1 0 0 0 0 2 3 3 0 0 1 3 3v8a3 3 0 0 1-3 3 1 1 0 0 0 0 2 5 5 0 0 0 5-5V8a5 5 0 0 0-5-5Z"
                                        ></path>
                                    </svg>
                                </button>
                            </HoverTooltip>
                        </div>
                    </div>
                    <div className="flex flex-col w-full h-[400px] gap-1 py-1 overflow-y-auto custom-scrollbar">
                        {isLoadingContractPosition ? (
                            <div className="flex w-full h-full items-center justify-center">
                                <LoadingIcon />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1 text-[11px] items-start justify-center">
                                {filteredList
                                    ? filteredList.map((row, index) => (
                                          <PerformanceDocsAddFromContractPopupTableRow
                                              key={row.guid}
                                              contractPosition={row}
                                              currencyList={props.currencyList}
                                              selected={selectedItems.includes(
                                                  row.guid ?? ''
                                              )}
                                              theme={props.theme}
                                              onClick={(
                                                  contract_position_guid
                                              ) => {
                                                  handleCheckboxChange(
                                                      contract_position_guid
                                                  )
                                              }}
                                              onRightClick={(
                                                  contract_position_guid
                                              ) => {
                                                  handleRightClickCheckboxChange(
                                                      contract_position_guid
                                                  )
                                              }}
                                          />
                                      ))
                                    : ''}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex flex-row text-right justify-end items-center gap-2">
                    <button
                        className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                        onClick={
                            props.handlePerformanceDocsAddFromContractPopup
                        }
                    >
                        Отмена
                    </button>

                    <button
                        className={`flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs 
                            ${selectedItems.length > 0 ? 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]' : 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#f6f6f6] dark:bg-[#494849]'}
                        `}
                        onClick={() => {
                            const map = new Map<string, ContractPosition>()
                            ;(contractPosition ?? []).forEach((r) =>
                                map.set(r.guid ?? '', r)
                            )
                            const rows = selectedItems
                                .map((g) => map.get(g))
                                .filter((x): x is ContractPosition => !!x)
                            props.onAddItems(rows)
                            props.handlePerformanceDocsAddFromContractPopup()
                        }}
                        disabled={!(selectedItems.length > 0)}
                    >
                        Добавить
                    </button>
                </div>
            </div>
        </div>
    )
}
export default PerformanceDocsAddFromContractPopup
